import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { formatError } from '../utils/errorHandler';
import FortnoxReferencePicker from './FortnoxReferencePicker';
import './LocationFortnoxBillingSection.css';

const emptyArticleRow = () => ({
  key: `${Date.now()}-${Math.random()}`,
  articleNumber: '',
  quantity: 1,
  label: ''
});

const LocationFortnoxBillingSection = forwardRef(({ locationId, disabled = false }, ref) => {
  const { t } = useTranslation(['locations', 'common']);
  const [loading, setLoading] = useState(true);
  const [fortnoxStatus, setFortnoxStatus] = useState(null);
  const [customerNumber, setCustomerNumber] = useState(null);
  const [articleRows, setArticleRows] = useState([]);
  const [billingMeta, setBillingMeta] = useState({
    invoiceReady: false,
    billingStatus: 'none',
    incompleteReason: null
  });
  const [sectionError, setSectionError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setSectionError('');
      try {
        const [statusRes, billingRes] = await Promise.all([
          axios.get('/api/integrations/fortnox/status'),
          axios.get(`/api/locations/${locationId}/fortnox-billing`)
        ]);
        if (cancelled) {
          return;
        }
        setFortnoxStatus(statusRes.data?.data || null);
        const billing = billingRes.data?.data || {};
        setCustomerNumber(billing.customerNumber || null);
        setArticleRows(
          (billing.articles || []).length > 0
            ? billing.articles.map((row) => ({
              key: row.articleNumber,
              articleNumber: row.articleNumber,
              quantity: row.quantity,
              label: row.label || ''
            }))
            : [emptyArticleRow()]
        );
        setBillingMeta({
          invoiceReady: billing.invoiceReady,
          billingStatus: billing.billingStatus || 'none',
          incompleteReason: billing.incompleteReason || null
        });
      } catch (err) {
        if (!cancelled) {
          setSectionError(formatError(err) || t('fortnoxBillingLoadFailed'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (locationId) {
      load();
    }

    return () => {
      cancelled = true;
    };
  }, [locationId, t]);

  const fortnoxReady = fortnoxStatus?.connected && !fortnoxStatus?.needsReconnect;

  const saveBilling = useCallback(async () => {
    setSectionError('');
    try {
      const response = await axios.put(`/api/locations/${locationId}/fortnox-billing`, {
        customerNumber: customerNumber || null,
        articles: articleRows
          .filter((row) => row.articleNumber)
          .map((row) => ({
            articleNumber: row.articleNumber,
            quantity: Number(row.quantity) || 1,
            label: row.label || null
          }))
      });
      const billing = response.data?.data || {};
      setBillingMeta({
        invoiceReady: billing.invoiceReady,
        billingStatus: billing.billingStatus || 'none',
        incompleteReason: billing.incompleteReason || null
      });
      return true;
    } catch (err) {
      setSectionError(formatError(err) || t('fortnoxBillingSaveFailed'));
      return false;
    }
  }, [locationId, customerNumber, articleRows, t]);

  useImperativeHandle(ref, () => ({ save: saveBilling }), [saveBilling]);

  const updateArticleRow = (key, patch) => {
    setArticleRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const addArticleRow = () => {
    setArticleRows((prev) => [...prev, emptyArticleRow()]);
  };

  const removeArticleRow = (key) => {
    setArticleRows((prev) => {
      const next = prev.filter((row) => row.key !== key);
      return next.length > 0 ? next : [emptyArticleRow()];
    });
  };

  if (loading) {
    return <p>{t('fortnoxBillingLoading')}</p>;
  }

  return (
    <div className="location-fortnox-billing">
      <h4>{t('fortnoxBillingTitle')}</h4>
      <p className="section-description">{t('fortnoxBillingDescription')}</p>

      {!fortnoxReady && (
        <div className="fortnox-billing-warning" role="status">
          <p>{t('fortnoxBillingNotConnected')}</p>
          <Link to="/my-company#fortnox">{t('fortnoxBillingGoIntegrations')}</Link>
        </div>
      )}

      {billingMeta.billingStatus === 'incomplete' && (
        <div className="fortnox-billing-warning" role="status">
          <p>{billingMeta.incompleteReason || t('fortnoxBillingIncomplete')}</p>
        </div>
      )}

      {sectionError && <div className="error">{sectionError}</div>}

      <div className="form-group">
        <label>{t('fortnoxCustomerLabel')}</label>
        <FortnoxReferencePicker
          type="customer"
          value={customerNumber}
          onChange={setCustomerNumber}
          disabled={disabled || !fortnoxReady}
          placeholder={t('fortnoxCustomerPlaceholder')}
        />
      </div>

      <div className="form-group">
        <label>{t('fortnoxDefaultArticlesLabel')}</label>
        <div className="fortnox-article-rows">
          {articleRows.map((row) => (
            <div key={row.key} className="fortnox-article-row">
              <FortnoxReferencePicker
                type="article"
                value={row.articleNumber || null}
                onChange={(value) => updateArticleRow(row.key, { articleNumber: value || '' })}
                disabled={disabled || !fortnoxReady}
                placeholder={t('fortnoxArticlePlaceholder')}
              />
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={row.quantity}
                onChange={(e) => updateArticleRow(row.key, { quantity: e.target.value })}
                disabled={disabled || !fortnoxReady}
                aria-label={t('fortnoxQuantityLabel')}
              />
              <input
                type="text"
                value={row.label}
                onChange={(e) => updateArticleRow(row.key, { label: e.target.value })}
                placeholder={t('fortnoxArticleLabelPlaceholder')}
                disabled={disabled || !fortnoxReady}
                aria-label={t('fortnoxArticleLabelPlaceholder')}
              />
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => removeArticleRow(row.key)}
                disabled={disabled || !fortnoxReady}
              >
                {t('common:delete')}
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={addArticleRow}
          disabled={disabled || !fortnoxReady}
        >
          {t('fortnoxAddArticleRow')}
        </button>
      </div>

      {billingMeta.billingStatus === 'ready' && (
        <p className="fortnox-billing-ready">{t('fortnoxBillingReady')}</p>
      )}
    </div>
  );
});

LocationFortnoxBillingSection.displayName = 'LocationFortnoxBillingSection';

export default LocationFortnoxBillingSection;
